import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  addDays, addMonths, endOfMonth, endOfWeek, format, isSameDay, isSameMonth,
  isToday, startOfMonth, startOfWeek,
} from 'date-fns';
import { de, enUS, tr as trLocale, nl, fr, es, it, pl, pt } from 'date-fns/locale';
import { Check, ChevronLeft, ChevronRight, Loader2, Plus, Trash2, Users, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import useAuthStore from '@/stores/authStore';
import calendarService, { type CalendarEvent, type EventColor } from '@/services/calendar.service';

const LOCALES: Record<string, any> = { de, en: enUS, tr: trLocale, nl, fr, es, it, pl, pt };
const COLORS: EventColor[] = ['blue', 'green', 'violet', 'amber', 'red', 'slate'];

/**
 * Kalender — Monatsansicht + Terminliste, Designsprache der übrigen Seiten
 * (Seitentokens .cal-page hell/dunkel, Eyebrow, Pill-Navigation).
 *
 * Sichtbar: eigene Termine + Einladungen. Gemeinsame Planung über die
 * Kollegen-Auswahl im Termin-Dialog; Eingeladene können zu-/absagen.
 */
export default function Calendar() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const locale = LOCALES[i18n.language?.slice(0, 2)] || de;

  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState<CalendarEvent | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<CalendarEvent | null>(null);

  // Form
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [timeFrom, setTimeFrom] = useState('09:00');
  const [timeTo, setTimeTo] = useState('10:00');
  const [allDay, setAllDay] = useState(false);
  const [color, setColor] = useState<EventColor>('blue');
  const [attendeeIds, setAttendeeIds] = useState<Set<string>>(new Set());

  const gridStart = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });

  const { data: events, isLoading } = useQuery({
    queryKey: ['calendar', gridStart.toISOString()],
    queryFn: () => calendarService.list(gridStart, addDays(gridEnd, 1)),
    refetchInterval: 60_000,
  });

  const { data: colleagues } = useQuery({
    queryKey: ['calendar-colleagues'],
    queryFn: () => calendarService.colleagues(),
    enabled: editorOpen,
    staleTime: 5 * 60_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['calendar'] });

  const days = useMemo(() => {
    const out: Date[] = [];
    for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) out.push(d);
    return out;
  }, [gridStart.getTime(), gridEnd.getTime()]);

  const eventsOfDay = (day: Date) =>
    (events || []).filter((e) => {
      const s = new Date(e.starts_at);
      const en = new Date(e.ends_at);
      return s <= addDays(day, 1) && en > day && (isSameDay(s, day) || (s < day && en > day));
    });

  const openCreate = (day?: Date) => {
    setEditing(null);
    setTitle(''); setDescription('');
    setDate(format(day || new Date(), 'yyyy-MM-dd'));
    setTimeFrom('09:00'); setTimeTo('10:00');
    setAllDay(false); setColor('blue'); setAttendeeIds(new Set());
    setEditorOpen(true);
  };

  const openEdit = (e: CalendarEvent) => {
    setEditing(e);
    setTitle(e.title); setDescription(e.description || '');
    const s = new Date(e.starts_at); const en = new Date(e.ends_at);
    setDate(format(s, 'yyyy-MM-dd'));
    setTimeFrom(format(s, 'HH:mm')); setTimeTo(format(en, 'HH:mm'));
    setAllDay(e.all_day); setColor((e.color as EventColor) || 'blue');
    setAttendeeIds(new Set(e.attendees.map((a) => a.user_id)));
    setSelected(null);
    setEditorOpen(true);
  };

  const buildInput = () => {
    const starts = allDay ? new Date(`${date}T00:00:00`) : new Date(`${date}T${timeFrom}:00`);
    const ends = allDay ? new Date(`${date}T23:59:59`) : new Date(`${date}T${timeTo}:00`);
    return {
      title: title.trim(), description: description.trim() || undefined,
      starts_at: starts.toISOString(), ends_at: ends.toISOString(),
      all_day: allDay, color, attendee_ids: [...attendeeIds],
    };
  };

  const saveMutation = useMutation({
    mutationFn: () => (editing
      ? calendarService.update(editing.id, buildInput())
      : calendarService.create(buildInput())),
    onSuccess: () => {
      setEditorOpen(false);
      invalidate();
      toast.success(t('shell:cal.saved', 'Termin gespeichert'));
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => calendarService.remove(id),
    onSuccess: () => { setSelected(null); invalidate(); toast.success(t('shell:cal.deleted', 'Termin gelöscht')); },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen')),
  });

  const respondMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'accepted' | 'declined' }) =>
      calendarService.respond(id, status),
    onSuccess: () => { setSelected(null); invalidate(); },
  });

  const upcoming = useMemo(() => {
    const now = new Date();
    return (events || [])
      .filter((e) => new Date(e.ends_at) >= now)
      .sort((a, b) => +new Date(a.starts_at) - +new Date(b.starts_at))
      .slice(0, 6);
  }, [events]);

  const fmtRange = (e: CalendarEvent) => {
    const s = new Date(e.starts_at); const en = new Date(e.ends_at);
    if (e.all_day) return format(s, 'EEE d. MMM', { locale });
    return `${format(s, 'EEE d. MMM HH:mm', { locale })} – ${format(en, 'HH:mm', { locale })}`;
  };

  return (
    <div className="cal-page">
      <header className="cal-head">
        <div className="min-w-0">
          <div className="cal-eyebrow">{t('shell:cal.eyebrow', 'Planung')}</div>
          <h1>{t('shell:cal.title', 'Kalender')}</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="cal-nav">
            <button type="button" onClick={() => setCursor(addMonths(cursor, -1))} aria-label="prev"><ChevronLeft className="h-4 w-4" /></button>
            <span className="cal-nav-month">{format(cursor, 'MMMM yyyy', { locale })}</span>
            <button type="button" onClick={() => setCursor(addMonths(cursor, 1))} aria-label="next"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <button type="button" className="cal-today-btn" onClick={() => setCursor(startOfMonth(new Date()))}>
            {t('shell:cal.today', 'Heute')}
          </button>
          <button type="button" className="cal-new-btn" onClick={() => openCreate()}>
            <Plus className="h-4 w-4" />
            <span className="max-sm:hidden">{t('shell:cal.new', 'Neuer Termin')}</span>
          </button>
        </div>
      </header>

      <div className="cal-body">
        <div className="cal-grid-wrap">
          {/* Haftentage */}
          <div className="cal-weekdays">
            {days.slice(0, 7).map((d) => (
              <span key={d.toISOString()}>{format(d, 'EEEEEE', { locale })}</span>
            ))}
          </div>

          {isLoading ? (
            <div className="flex flex-1 items-center justify-center" style={{ color: 'var(--cal-muted)' }}>
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <div className="cal-grid">
              {days.map((day) => {
                const dayEvents = eventsOfDay(day);
                return (
                  <div
                    key={day.toISOString()}
                    className={cn('cal-cell', !isSameMonth(day, cursor) && 'is-outside', isToday(day) && 'is-today')}
                    onDoubleClick={() => openCreate(day)}
                  >
                    <span className="cal-cell-num">{format(day, 'd')}</span>
                    <div className="cal-cell-events">
                      {dayEvents.slice(0, 3).map((e) => (
                        <button
                          key={e.id}
                          type="button"
                          className={cn('cal-chip', `is-${e.color || 'blue'}`, e.my_status === 'declined' && 'is-declined')}
                          onClick={() => setSelected(e)}
                          title={e.title}
                        >
                          {!e.all_day && <span className="cal-chip-time">{format(new Date(e.starts_at), 'HH:mm')}</span>}
                          <span className="cal-chip-title">{e.title}</span>
                        </button>
                      ))}
                      {dayEvents.length > 3 && (
                        <span className="cal-more">+{dayEvents.length - 3}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Anstehend — sağ ray */}
        <aside className="cal-rail">
          <div className="cal-rail-title">{t('shell:cal.upcoming', 'Anstehend')}</div>
          {!upcoming.length ? (
            <p className="cal-rail-empty">{t('shell:cal.empty', 'Keine anstehenden Termine')}</p>
          ) : upcoming.map((e) => (
            <button key={e.id} type="button" className="cal-rail-item" onClick={() => setSelected(e)}>
              <span className={cn('cal-dot', `is-${e.color || 'blue'}`)} />
              <span className="min-w-0 flex-1">
                <span className="cal-rail-item-title">{e.title}</span>
                <span className="cal-rail-item-time">{fmtRange(e)}</span>
              </span>
              {e.my_status === 'invited' && <span className="cal-invited-dot" title={t('shell:cal.invitedYou', 'Einladung offen')} />}
            </button>
          ))}
        </aside>
      </div>

      {/* Detay */}
      <Dialog open={Boolean(selected)} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-sm rounded-xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className={cn('cal-dot', `is-${selected.color || 'blue'}`)} />
                  {selected.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <p className="text-slate-600 dark:text-slate-300">{fmtRange(selected)}</p>
                {selected.description && (
                  <p className="whitespace-pre-wrap text-slate-500">{selected.description}</p>
                )}
                <p className="text-xs text-slate-400">
                  {t('shell:cal.by', 'Von')} {[selected.creator_first_name, selected.creator_last_name].filter(Boolean).join(' ')}
                </p>
                {selected.attendees.length > 0 && (
                  <div className="space-y-1 rounded-lg border border-slate-200 p-2 dark:border-white/10">
                    {selected.attendees.map((a) => (
                      <div key={a.user_id} className="flex items-center justify-between text-xs">
                        <span>{a.name}</span>
                        <span className={cn(
                          'font-semibold',
                          a.status === 'accepted' ? 'text-emerald-600' : a.status === 'declined' ? 'text-red-500' : 'text-slate-400',
                        )}>
                          {a.status === 'accepted' ? '✓' : a.status === 'declined' ? '✕' : '?'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter className="flex-row justify-between sm:justify-between">
                <div className="flex gap-1.5">
                  {selected.is_mine && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openEdit(selected)}>
                        {t('common:action.edit', 'Bearbeiten')}
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteMutation.mutate(selected.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
                {!selected.is_mine && selected.my_status && (
                  <div className="flex gap-1.5">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => respondMutation.mutate({ id: selected.id, status: 'accepted' })}>
                      <Check className="mr-1 h-3.5 w-3.5" />{t('shell:cal.accept', 'Zusagen')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => respondMutation.mutate({ id: selected.id, status: 'declined' })}>
                      <X className="mr-1 h-3.5 w-3.5" />{t('shell:cal.decline', 'Absagen')}
                    </Button>
                  </div>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Oluştur / Düzenle */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle>{editing ? t('shell:cal.edit', 'Termin bearbeiten') : t('shell:cal.new', 'Neuer Termin')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3.5">
            <div className="space-y-1.5">
              <Label htmlFor="cal-title">{t('shell:cal.titleLabel', 'Titel')}</Label>
              <Input id="cal-title" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('shell:cal.titlePlaceholder', 'z. B. Team-Abstimmung')} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5">
                <Label>{t('shell:cal.date', 'Datum')}</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>{t('shell:cal.from', 'Von')}</Label>
                <Input type="time" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)} disabled={allDay} />
              </div>
              <div className="space-y-1.5">
                <Label>{t('shell:cal.to', 'Bis')}</Label>
                <Input type="time" value={timeTo} onChange={(e) => setTimeTo(e.target.value)} disabled={allDay} />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <Checkbox checked={allDay} onCheckedChange={(v) => setAllDay(v === true)} />
                {t('shell:cal.allDay', 'Ganztägig')}
              </label>
              <div className="flex gap-1.5">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={cn('cal-color-pick', `is-${c}`, color === c && 'is-active')}
                    aria-label={c}
                  />
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cal-desc">{t('shell:cal.notes', 'Notizen (optional)')}</Label>
              <Textarea id="cal-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                {t('shell:cal.invite', 'Kollegen einladen')}
              </Label>
              <div className="max-h-36 space-y-1 overflow-y-auto rounded-xl border border-slate-200 p-2 dark:border-white/10">
                {(colleagues || []).filter((c) => c.id !== user?.id).map((c) => {
                  const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email;
                  const checked = attendeeIds.has(c.id);
                  return (
                    <label key={c.id} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-white/5">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          const next = new Set(attendeeIds);
                          if (v === true) next.add(c.id); else next.delete(c.id);
                          setAttendeeIds(next);
                        }}
                      />
                      <span className="min-w-0 flex-1 truncate">{name}</span>
                      <span className="text-[10px] uppercase text-slate-400">{c.role}</span>
                    </label>
                  );
                })}
                {!colleagues?.length && (
                  <p className="px-2 py-1.5 text-xs text-slate-500">{t('shell:meet.noColleagues', 'Keine weiteren Desk-Nutzer in dieser Organisation.')}</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>{t('common:action.cancel', 'Abbrechen')}</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !title.trim() || !date}>
              {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common:action.save', 'Speichern')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
